import { useEffect } from 'react';

import { THEMES } from './constants/portfolio';

// Progressively upgrades the theme carousels to 4K: each slide first paints
// the size the adapter resolved (2K, or Moodie's shared 1024), and once the
// slide is actually on screen the next tier is fetched. Swapping <img> src in
// place keeps the current bitmap visible until the new one is decoded, which
// is exactly the "2K placeholder, replace with 4K when loaded" behaviour.
//
// The web-shared-components templates all render plain <img> tags off
// images[0], so this has to happen at the DOM level:
//  - MOODIE: grid list and lightbox share images[0] (1024), so the lightbox
//    slides (the only Moodie <img>s inside react-slick's .slick-slide) chain
//    1024 -> 2048 -> 4K while the grid stays 1024
//  - ADAMS: gallery slides chain 2048 -> 4K; gated to /gallery/ because the
//    home folder carousel is also react-slick showing the same 2K urls and
//    must stay 2K
//  - NEWTON: the gallery is a scroll box of plain <img>s (no slick), so it is
//    gated to /gallery/ only
// Off-screen slides never intersect the viewport, so they don't pull 4K.

const isSlideshowViewport = () => typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia('(min-width: 768px)').matches;

const UPGRADE_CHAINS = {
  [THEMES.MOODIE]: ['size_1024', 'size_2048', 'size_4k'],
  [THEMES.NEWTON]: ['size_2048', 'size_4k'],
  [THEMES.ADAMS]: ['size_2048', 'size_4k'],
};

// Map of currently-rendered url -> next tier url for every carousel photo.
// Tiers that resolve to the same url (test env aliases sizes) are skipped.
export const buildCarouselHiResUpgrades = (portfolio) => {
  const upgrades = new Map();
  const chain = UPGRADE_CHAINS[portfolio?.theme?.name?.toUpperCase()];
  if (!chain || !isSlideshowViewport()) return upgrades;
  (portfolio?.folders || []).forEach((folder) => {
    (folder?.photos?.edges || []).forEach((edge) => {
      const urls = edge?.node?.urls;
      if (!urls) return;
      for (let i = 0; i < chain.length - 1; i += 1) {
        const from = urls[chain[i]];
        const to = urls[chain[i + 1]];
        if (from && to && from !== to && !upgrades.has(from)) {
          upgrades.set(from, to);
        }
      }
    });
  });
  return upgrades;
};

const isCarouselImage = (img, themeName) => {
  if (themeName === THEMES.MOODIE) return !!img.closest('.slick-slide');
  if (!window.location.pathname.includes('/gallery/')) return false;
  if (themeName === THEMES.ADAMS) return !!img.closest('.slick-slide');
  return true;
};

const useCarouselHiResUpgrade = (portfolio) => {
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;
    const upgrades = buildCarouselHiResUpgrades(portfolio);
    if (!upgrades.size) return undefined;
    const themeName = portfolio?.theme?.name?.toUpperCase();
    const downgrades = new Map();
    upgrades.forEach((to, from) => downgrades.set(to, from));
    const failedUrls = new Set();

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(({ isIntersecting, target }) => {
        if (!isIntersecting) return;
        observer.unobserve(target);
        const next = upgrades.get(target.getAttribute('src'));
        if (next && !failedUrls.has(next)) target.setAttribute('src', next);
      });
    });

    // load/error don't bubble, but they do reach document in capture phase
    const onLoad = (event) => {
      const img = event.target;
      if (!img || img.tagName !== 'IMG') return;
      if (!upgrades.has(img.getAttribute('src'))) return;
      if (!isCarouselImage(img, themeName)) return;
      observer.observe(img);
    };

    // A failed hi-res fetch would blank the <img>, so remember the broken url
    // and drop back to the tier that was already on screen
    const onError = (event) => {
      const img = event.target;
      if (!img || img.tagName !== 'IMG') return;
      const current = img.getAttribute('src');
      const previous = downgrades.get(current);
      if (!previous) return;
      failedUrls.add(current);
      img.setAttribute('src', previous);
    };

    document.addEventListener('load', onLoad, true);
    document.addEventListener('error', onError, true);

    // Images already in the DOM (direct entry on a gallery url) may have
    // finished loading before this effect attached the listener, so sweep
    // anything already complete into the observer as well
    Array.prototype.forEach.call(document.images, (img) => {
      if (!img.complete) return;
      if (!upgrades.has(img.getAttribute('src'))) return;
      if (!isCarouselImage(img, themeName)) return;
      observer.observe(img);
    });

    return () => {
      document.removeEventListener('load', onLoad, true);
      document.removeEventListener('error', onError, true);
      observer.disconnect();
    };
  }, [portfolio]);
};

export default useCarouselHiResUpgrade;
